const FISCAL_YEAR_START_MONTH = 7;

export const CATEGORY_CHART_LIMIT = 10;

export const getCurrentFiscalYear = (baseDate = new Date()) => {
  const currentMonth = baseDate.getMonth() + 1;
  const currentYear = baseDate.getFullYear();
  return currentMonth >= FISCAL_YEAR_START_MONTH ? currentYear : currentYear - 1;
};

export const buildFiscalYearOptions = (centerYear = getCurrentFiscalYear(), span = 5) => {
  return Array.from({ length: (span * 2) + 1 }, (_, index) => centerYear - span + index);
};

export const buildFiscalMonthOptions = (fiscalYear) => {
  const baseYear = Number(fiscalYear) || getCurrentFiscalYear();
  const latterHalf = Array.from({ length: 6 }, (_, index) => {
    const month = index + FISCAL_YEAR_START_MONTH;
    return {
      value: String(month),
      label: `${baseYear}年${month}月`,
    };
  });

  const firstHalf = Array.from({ length: 6 }, (_, index) => {
    const month = index + 1;
    return {
      value: String(month),
      label: `${baseYear + 1}年${month}月`,
    };
  });

  return [
    { value: '', label: '通年' },
    ...latterHalf,
    ...firstHalf,
  ];
};

const padMonth = (month) => String(month).padStart(2, '0');

export const formatYmValue = (year, month) => `${year}-${padMonth(month)}`;

export const parseYmValue = (ym) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return null;

  const [year, month] = ym.split('-').map(Number);
  if (!year || month < 1 || month > 12) return null;

  return { year, month };
};

export const formatYmLabel = (ym) => {
  const parsed = parseYmValue(ym);
  if (!parsed) return '';

  return `${parsed.year}年${parsed.month}月`;
};

export const compareYmValue = (leftYm, rightYm) => {
  const left = parseYmValue(leftYm);
  const right = parseYmValue(rightYm);
  if (!left || !right) return 0;

  const leftKey = (left.year * 100) + left.month;
  const rightKey = (right.year * 100) + right.month;

  if (leftKey === rightKey) return 0;
  return leftKey > rightKey ? 1 : -1;
};

export const addMonthsToYm = (ym, monthOffset) => {
  const parsed = parseYmValue(ym);
  if (!parsed) return '';

  const date = new Date(parsed.year, parsed.month - 1, 1);
  date.setMonth(date.getMonth() + monthOffset);
  return formatYmValue(date.getFullYear(), date.getMonth() + 1);
};

export const countMonthsInRange = (startYm, endYm) => {
  const normalized = normalizeYmRange(startYm, endYm);
  if (!normalized.startYm || !normalized.endYm) return 0;

  const start = parseYmValue(normalized.startYm);
  const end = parseYmValue(normalized.endYm);
  return ((end.year - start.year) * 12) + (end.month - start.month) + 1;
};

export const normalizeYmRange = (startYm, endYm) => {
  if (!startYm && !endYm) {
    return { startYm: '', endYm: '' };
  }

  if (!startYm) {
    return { startYm: endYm, endYm };
  }

  if (!endYm) {
    return { startYm, endYm: startYm };
  }

  return compareYmValue(startYm, endYm) <= 0
    ? { startYm, endYm }
    : { startYm: endYm, endYm: startYm };
};

export const getDefaultYmRange = (baseDate = new Date()) => {
  const fiscalYear = getCurrentFiscalYear(baseDate);
  return {
    startYm: formatYmValue(fiscalYear, FISCAL_YEAR_START_MONTH),
    endYm: formatYmValue(fiscalYear + 1, 6),
  };
};

export const buildPresetRanges = (baseDate = new Date()) => {
  const currentMonthYm = formatYmValue(baseDate.getFullYear(), baseDate.getMonth() + 1);
  const currentFiscalRange = getDefaultYmRange(baseDate);

  return [
    {
      key: 'currentFiscalYear',
      label: '今期',
      range: currentFiscalRange,
    },
    {
      key: 'last6Months',
      label: '直近6か月',
      range: {
        startYm: addMonthsToYm(currentMonthYm, -5),
        endYm: currentMonthYm,
      },
    },
    {
      key: 'last12Months',
      label: '直近12か月',
      range: {
        startYm: addMonthsToYm(currentMonthYm, -11),
        endYm: currentMonthYm,
      },
    },
  ];
};

export const formatPeriodLabel = (startYm, endYm) => {
  const normalized = normalizeYmRange(startYm, endYm);
  if (!normalized.startYm || !normalized.endYm) return '';
  if (normalized.startYm === normalized.endYm) return formatYmLabel(normalized.startYm);
  return `${formatYmLabel(normalized.startYm)} - ${formatYmLabel(normalized.endYm)}`;
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
};

export const formatCompactCurrency = (value) => {
  const amount = Number(value || 0);

  if (Math.abs(amount) >= 100000000) {
    return `${(amount / 100000000).toFixed(1).replace(/\.0$/, '')}億円`;
  }

  if (Math.abs(amount) >= 10000) {
    return `${(amount / 10000).toFixed(1).replace(/\.0$/, '')}万円`;
  }

  return formatCurrency(amount);
};

export const formatPercent = (value) => {
  return `${Number(value || 0).toFixed(1)}%`;
};

export const formatDateTime = (dateValue) => {
  if (!dateValue) return '';

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateValue);
};

export const normalizeAggregateItems = ({ items: apiItems = [], labels = [], values = [], tab }) => {
  const itemCount = Math.min(labels.length, values.length);
  const responseItems = Array.isArray(apiItems) && apiItems.length
    ? apiItems.map((item, index) => ({
        key: item.id == null ? `unassigned-${index}` : `customer-${item.id}`,
        label: item.label || '未設定',
        value: Number(item.value || 0),
      }))
    : Array.from({ length: itemCount }, (_, index) => ({
        key: `aggregate-${index}`,
        label: labels[index] || '未設定',
        value: Number(values[index] || 0),
      }));
  const baseItems = responseItems.filter((item) => item.label);

  const sortedItems = tab.isMonthly
    ? baseItems
    : [...baseItems].sort((left, right) => right.value - left.value || left.label.localeCompare(right.label, 'ja'));

  const total = sortedItems.reduce((sum, item) => sum + item.value, 0);
  let runningTotal = 0;

  const items = sortedItems.map((item, index) => {
    runningTotal += item.value;
    const share = total > 0 ? (item.value / total) * 100 : 0;
    const cumulativeShare = total > 0 ? (runningTotal / total) * 100 : 0;

    return {
      ...item,
      rank: index + 1,
      share,
      cumulativeShare,
    };
  });

  return {
    items,
    total,
  };
};

export const createAggregateTabs = ({
  buildingFetcher,
  customerFetcher,
  vendorFetcher,
  businessFetcher,
  inChargeFetcher,
  monthFetcher,
}) => {
  return [
    {
      key: 'building',
      label: '建物別',
      shortDescription: '建物ごとの売上比較',
      chartTitle: '建物別 売上ランキング',
      longDescription: '建物単位の売上規模を比較し、重点管理すべき建物を把握できます。',
      tableLabel: '建物名',
      fetcher: buildingFetcher,
      isMonthly: false,
    },
    {
      key: 'customer',
      label: '顧客別',
      shortDescription: '本社・支店を個別に比較',
      chartTitle: '顧客別 売上ランキング（本社・支店別）',
      longDescription: '本社と支店をそれぞれ個別の顧客として比較できます。',
      tableLabel: '顧客名',
      fetcher: customerFetcher,
      isMonthly: false,
    },
    {
      key: 'parentCustomer',
      label: '本社別（支店含む）',
      shortDescription: '支店売上を本社へ合算',
      chartTitle: '本社別 売上ランキング（支店含む）',
      longDescription: '現在の顧客マスタの親子関係に基づき、本社自身と配下支店の売上を合算します。',
      tableLabel: '本社・単独顧客名',
      fetcher: (params = {}) => customerFetcher({ ...params, customer_grouping: 'parent' }),
      isMonthly: false,
    },
    {
      key: 'vendor',
      label: '業者別',
      shortDescription: '業者ごとの支払比較',
      chartTitle: '業者別 支払金額ランキング',
      longDescription: '業者別の金額を比較し、外注先への支払集中を把握できます。',
      tableLabel: '業者名',
      amountLabel: '支払金額',
      totalAmountLabel: '総支払金額',
      fetcher: vendorFetcher,
      isMonthly: false,
    },
    {
      key: 'business',
      label: '業務区分別',
      shortDescription: '業務区分ごとの売上比較',
      chartTitle: '業務区分別 売上ランキング',
      longDescription: '業務区分ごとの売上構成を確認し、強い業務領域と偏りを見極められます。',
      tableLabel: '業務区分',
      fetcher: businessFetcher,
      isMonthly: false,
    },
    {
      key: 'inCharge',
      label: '担当者別',
      shortDescription: '担当者ごとの売上比較',
      chartTitle: '担当者別 売上ランキング',
      longDescription: '担当者ごとの担当金額を比較し、負荷や成果の偏りを把握できます。',
      tableLabel: '担当者名',
      fetcher: inChargeFetcher,
      isMonthly: false,
    },
    {
      key: 'month',
      label: '月別',
      shortDescription: '年度内の売上推移',
      chartTitle: '月別 売上推移',
      longDescription: '月ごとの推移と平均線を重ねて、繁忙月や落ち込み月を視覚的に確認できます。',
      tableLabel: '月',
      fetcher: monthFetcher,
      isMonthly: true,
    },
  ];
};
